import {
  canTransitionIngestionState,
  transitionIngestionState,
} from "./ingestionState";
import type { IngestionState } from "./ingestion.types";
import {
  deriveUploadJobStatus,
  type UploadCheckpoint,
  type UploadFileItemSnapshot,
  type UploadJobEvent,
  type UploadJobSnapshot,
} from "./uploadJobContract";

export type UploadJobStore = Readonly<Record<string, UploadJobSnapshot>>;

export function createUploadJobStore(
  jobs: readonly UploadJobSnapshot[] = [],
): UploadJobStore {
  return Object.fromEntries(jobs.map((job) => [job.id, job]));
}

export function getUploadJob(
  store: UploadJobStore,
  jobId: string,
): UploadJobSnapshot | null {
  return store[jobId] ?? null;
}

export function listUploadJobs(store: UploadJobStore): UploadJobSnapshot[] {
  return Object.values(store).sort((left, right) =>
    right.updatedAt.localeCompare(left.updatedAt),
  );
}

/**
 * Replaces a local job with a newer server snapshot. Older snapshots are
 * ignored so a delayed refresh cannot roll the activity UI backward.
 */
export function reconcileUploadJobSnapshot(
  store: UploadJobStore,
  snapshot: UploadJobSnapshot,
): UploadJobStore {
  const current = store[snapshot.id];
  if (
    current &&
    (current.version > snapshot.version ||
      current.lastEventSequence > snapshot.lastEventSequence)
  ) {
    return store;
  }

  return { ...store, [snapshot.id]: snapshot };
}

function replaceItem(
  job: UploadJobSnapshot,
  fileItemId: string,
  update: (item: UploadFileItemSnapshot) => UploadFileItemSnapshot,
  event: UploadJobEvent,
): UploadJobSnapshot {
  let found = false;
  const items = job.items.map((item) => {
    if (item.id !== fileItemId) return item;
    found = true;
    return update({
      ...item,
      updatedAt: event.occurredAt,
      lastEventSequence: event.sequence,
    });
  });

  if (!found) {
    throw new Error(`Unknown upload file item: ${fileItemId}`);
  }

  return { ...job, items };
}

function stateForRetryCheckpoint(checkpoint: UploadCheckpoint): IngestionState {
  switch (checkpoint) {
    case "validation":
      return "queued";
    case "upload":
      return "uploading";
    case "processing":
      return "processing";
    case "saving":
      return "saving";
  }
}

function applyEventToJob(
  job: UploadJobSnapshot,
  event: UploadJobEvent,
): UploadJobSnapshot {
  let nextJob = {
    ...job,
    version: event.version,
    lastEventSequence: event.sequence,
    updatedAt: event.occurredAt,
  };

  switch (event.type) {
    case "job.created":
      return nextJob;
    case "job.handed_off":
      return {
        ...nextJob,
        status: "handed_off",
        handedOffAt: event.occurredAt,
      };
    case "job.acknowledged":
      return { ...nextJob, acknowledgedAt: event.payload.acknowledgedAt };
    case "file.state_changed": {
      nextJob = replaceItem(
        nextJob,
        event.fileItemId,
        (item) => {
          if (item.state !== event.payload.from) {
            if (item.state === event.payload.to) return item;
            throw new Error(
              `Upload file item ${item.id} expected state ${event.payload.from}, found ${item.state}`,
            );
          }
          return {
            ...item,
            state: transitionIngestionState(
              event.payload.from,
              event.payload.to,
            ),
            checkpoint: event.payload.checkpoint,
            failure: null,
          };
        },
        event,
      );
      break;
    }
    case "file.progressed":
      nextJob = replaceItem(
        nextJob,
        event.fileItemId,
        (item) => ({ ...item, progress: event.payload.progress }),
        event,
      );
      break;
    case "file.failed":
      nextJob = replaceItem(
        nextJob,
        event.fileItemId,
        (item) => {
          if (
            item.state !== event.payload.state &&
            !canTransitionIngestionState(item.state, event.payload.state)
          ) {
            throw new Error(
              `Invalid failure transition: ${item.state} -> ${event.payload.state}`,
            );
          }
          return {
            ...item,
            state: event.payload.state,
            checkpoint: event.payload.failure.checkpoint,
            failure: event.payload.failure,
          };
        },
        event,
      );
      break;
    case "file.retry_requested":
      nextJob = replaceItem(
        nextJob,
        event.fileItemId,
        (item) => {
          const nextState = stateForRetryCheckpoint(
            event.payload.fromCheckpoint,
          );
          if (
            item.state !== nextState &&
            !canTransitionIngestionState(item.state, nextState)
          ) {
            throw new Error(
              `Invalid retry transition: ${item.state} -> ${nextState}`,
            );
          }
          return {
            ...item,
            state: nextState,
            checkpoint: event.payload.fromCheckpoint,
            failure: null,
            progress: null,
          };
        },
        event,
      );
      break;
    case "file.canceled":
      nextJob = replaceItem(
        nextJob,
        event.fileItemId,
        (item) => ({
          ...item,
          state: transitionIngestionState(item.state, "canceled"),
          checkpoint: event.payload.checkpoint,
          failure: null,
        }),
        event,
      );
      break;
    case "file.saved":
      nextJob = replaceItem(
        nextJob,
        event.fileItemId,
        (item) => ({
          ...item,
          state: transitionIngestionState(item.state, "complete"),
          destination: event.payload.destination,
          failure: null,
        }),
        event,
      );
      break;
  }

  return {
    ...nextJob,
    status: deriveUploadJobStatus(nextJob.items),
  };
}

/**
 * Applies one ordered event. Duplicate/old events are idempotent; a sequence
 * gap throws so the caller can fetch a fresh snapshot before continuing.
 */
export function applyUploadJobEvent(
  store: UploadJobStore,
  event: UploadJobEvent,
): UploadJobStore {
  const current = store[event.jobId];
  if (!current) throw new Error(`Unknown upload job: ${event.jobId}`);
  if (event.sequence <= current.lastEventSequence) return store;
  if (event.sequence !== current.lastEventSequence + 1) {
    throw new Error(
      `Upload job event gap: expected ${current.lastEventSequence + 1}, received ${event.sequence}`,
    );
  }
  if (event.version < current.version) {
    throw new Error(
      `Upload job version regressed: expected at least ${current.version}, received ${event.version}`,
    );
  }

  return {
    ...store,
    [event.jobId]: applyEventToJob(current, event),
  };
}
